import React, { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import './OptimizedSepaView.less';
import { FormikValues } from 'formik';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import NavigationBar from '../../../../components/NavigationBar/NavigationBar';
import BrandButton from '../../../../components/BrandButton/BrandButton';
import {
  getBrandColor,
  LocalizePropType,
  UserDataOutput,
  formattedAddress,
  isLocalizeAdded,
  isKYCApp,
  maskSepa,
  isV2BankFlow,
  ibanPattern,
} from 'getaway-data-layer';
import { GA_TITLES, isDashboardScreen } from '../../../../../utils';
import Hyperlink from 'react-native-hyperlink';
import { Clear } from '@mui/icons-material';
// @ts-ignore
import { Linking } from 'react-native-web';
import ExpandableItem from '../../../../components/ExpandableItem/ExpandableItem';
import ListItem from '../../../../components/ListItem/ListItem';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import BrandIcon from '../../../../components/BrandIcon/BrandIcon';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { de } from 'date-fns/locale';

type Props = {
  localize: LocalizePropType;
  formik: FormikValues;
  user: UserDataOutput;
  onClose: () => void;
  expanded: boolean;
  onIbanChanged: (value: string) => void;
  onExpandPressed: (value: boolean) => void;
  addressStatus: any;
  address: any;
  onPressAddress: () => void;
  onPressAddressInProgress: () => void;
  onPressAddressCompleted: () => void;
  customError: string;
  maxDate: any;
  minDate: any;
  dateFormat: string;
  bicMandatory: boolean;
  onBicChange: (value: string) => void;
  v2OrgSignupEnabled: boolean;
  truelayerIban: string;
  deleteTruelayerIban: () => void;
  bankLoginEnabled: boolean;
  openTruelayer: (providerId: string) => void;
  clearIban: () => void;
  isBusinessBillingProfile: boolean;
  enableBillingProfile: boolean;
  setEnableBillingProfile: (arg: boolean) => void;
  onPressAddBillingAddress: () => void;
  billingInfo: any;
  existingPayment: any;
};

const BANKS = [
  {
    name: 'comdirekt',
    image: '/images/Comdirekt.png',
    providerId: 'xs2a-comdirect',
  },
  {
    name: 'commerzbank',
    image: '/images/Commerzbank.png',
    providerId: 'xs2a-commerzbank',
  },
  {
    name: 'deutschebank',
    image: '/images/DeutscheBank.png',
    providerId: 'xs2a-deutsche-bank',
  },
  {
    name: 'dkb',
    image: '/images/DKB.png',
    providerId: 'xs2a-deutschekredit-de',
  },
  {
    name: 'ing',
    image: '/images/ING.png',
    providerId: 'xs2a-ing-germany',
  },
  {
    name: 'postbank',
    image: '/images/Postbank.png',
    providerId: 'xs2a-postbank-de',
  },
  {
    name: 'spardabank',
    image: '/images/Sparda-Bank.png',
    providerId: 'xs2a-volksbanken-de',
  },
  {
    name: 'sparkasse',
    image: '/images/Sparkasse.png',
    providerId: 'xs2a-sparkasse',
  },
  {
    name: 'targobank',
    image: '/images/Targobank.png',
    providerId: 'xs2a-targobank-de',
  },
  {
    name: 'volksbank',
    image: '/images/Volksbank.png',
    providerId: 'xs2a-volksbanken-de',
  },
  {
    name: 'santander',
    image: '/images/Santander.png',
    providerId: 'xs2a-santander-de',
  },
];
//

const OptimizedSepaView = ({
  localize,
  formik,
  user,
  onClose,
  expanded,
  onIbanChanged,
  onExpandPressed,
  addressStatus,
  address,
  onPressAddress,
  onPressAddressInProgress,
  onPressAddressCompleted,
  customError,
  dateFormat,
  maxDate,
  minDate,
  bicMandatory,
  onBicChange,
  v2OrgSignupEnabled,
  truelayerIban,
  deleteTruelayerIban,
  bankLoginEnabled,
  openTruelayer,
  clearIban,
  isBusinessBillingProfile,
  enableBillingProfile,
  setEnableBillingProfile,
  onPressAddBillingAddress,
  billingInfo,
  existingPayment,
}: Props) => {
  const [isOpened, setIsOpened] = React.useState(true);

  const isUserInfoFilled =
    (user?.firstName || formik.values?.firstName) &&
    (user?.lastName || formik.values?.lastName) &&
    (user?.userType === 'org' ||
      user?.dateOfBirth ||
      formik.values?.dateOfBirth) &&
    (user?.address?.city || formik.values?.address?.city);
  const datePickerRef = useRef(null);
  const pattern = ibanPattern(formik?.values?.iban);
  const isValidIban = Boolean(
    formik?.values?.iban && pattern.test(formik?.values?.iban)
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const container = document.querySelector('[data-testid="DoBView-dob"]');
      if (container) {
        const inputElement = container.querySelector('input');
        if (inputElement) {
          inputElement.placeholder = localize('TT.MM.JJJJ');
        }
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (formik?.values?.iban) {
      setIsOpened(false);
    }
  }, [formik?.values?.iban]);

  return (
    <div className="screen-container">
      <NavigationBar
        TitleTestId="OptimizedSepaView-title"
        DescriptionTestId="OptimizedSepaView-description"
        title={localize('firstSteps.payment.optimized.sepa.title')}
        description={localize('firstSteps.payment.optimized.sepa.desc')}
        showHelp
        showArrowBack={
          !isKYCApp() ||
          (isKYCApp() &&
            (isDashboardScreen() || (bankLoginEnabled && !isV2BankFlow())))
        }
        onClose={onClose}
      />
      <div
        className={
          isLocalizeAdded(localize, 'sepa.optimized.faq')
            ? 'screen-body-faq'
            : 'screen-body'
        }
      >
        <div
          className={
            isLocalizeAdded(localize, 'sepa.optimized.faq')
              ? 'screen-body-content-faq'
              : 'screen-body-content'
          }
        >
          <Box component="form" noValidate onSubmit={formik.handleSubmit}>
            {truelayerIban || (!formik?.errors?.iban && isValidIban) ? (
              <>
                <ListItem
                  title={
                    truelayerIban
                      ? maskSepa(truelayerIban)
                      : formik?.values?.iban
                  }
                  leftItem={
                    <img
                      style={{
                        width: 20,
                        height: 20,
                        marginRight: 35,
                        objectFit: 'contain',
                      }}
                      src="/images/bank.png"
                      alt="user"
                    />
                  }
                  rightIcon="redTrash"
                  rightIconSize={16}
                  onClick={() => {
                    deleteTruelayerIban();
                    setIsOpened(true);
                  }}
                />
                {formik?.touched?.iban && Boolean(formik.errors.iban) && (
                  <div className="date-of-birth-error">
                    {formik.errors.iban}
                  </div>
                )}
              </>
            ) : (
              <TextField
                data-testid="OptimizedSepaView-iban"
                required
                fullWidth
                id="iban"
                className="sepa-form-input"
                label={localize('firstSteps.payment.sepa.iban.placeholder')}
                helperText={(formik.touched.iban && formik.errors.iban) || ''}
                error={formik.touched.iban && Boolean(formik.errors.iban)}
                variant="standard"
                autoComplete="iban"
                type="text"
                {...formik.getFieldProps('iban')}
                onChange={onIbanChanged}
                onFocus={() => setIsOpened(false)}
                InputProps={{
                  endAdornment: formik.values.iban && (
                    <InputAdornment position="end">
                      <IconButton onClick={clearIban} edge="end" size="small">
                        <Clear sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {bicMandatory &&
              formik?.values?.iban &&
              formik?.values?.iban?.length > 2 &&
              !formik?.values?.iban?.startsWith('DE') && (
                <TextField
                  fullWidth
                  id="bic"
                  className="bic-form-input"
                  label={localize('firstSteps.payment.bic.placeholder')}
                  helperText={(formik.touched.bic && formik.errors.bic) || ''}
                  error={formik.touched.bic && Boolean(formik.errors.bic)}
                  variant="standard"
                  autoComplete="bic"
                  type="text"
                  {...formik.getFieldProps('bic')}
                  onChange={onBicChange}
                />
              )}

            {bankLoginEnabled && isV2BankFlow() && (
              <div>
                <ExpandableItem
                  value={
                    isValidIban ? null : (
                      <img
                        style={{
                          width: 'auto',
                          height: 18,
                          objectFit: 'contain',
                          position: 'relative',
                        }}
                        src="/images/truelayer.png"
                        alt="truelayer"
                      />
                    )
                  }
                  containerStyles={{ marginTop: 8 }}
                  titleClassName={
                    isValidIban
                      ? 'payment-user-header-disabled'
                      : 'payment-user-header'
                  }
                  title={localize('payment.bank.header')}
                  showSeparator
                  customIsOpened={isOpened}
                  onClickOver={() => setIsOpened(!isOpened)}
                  disabled={isValidIban}
                >
                  {BANKS.map((bank) => (
                    <ListItem
                      key={bank.name}
                      title={localize(`payment.bank.${bank.name}`)}
                      leftItem={
                        <img
                          style={{
                            width: 26,
                            height: 26,
                            marginRight: 35,
                            objectFit: 'contain',
                            position: 'relative',
                          }}
                          src={bank.image}
                        />
                      }
                      rightIcon="arrowRight"
                      rightIconSize={16}
                      onClick={() => openTruelayer(bank.providerId)}
                    />
                  ))}
                </ExpandableItem>
              </div>
            )}
            <ExpandableItem
              value={''}
              containerStyles={{
                marginTop: bankLoginEnabled && isV2BankFlow() ? 8 : 0,
              }}
              titleClassName={'payment-user-header'}
              title={localize('firstSteps.payment.sepa.info')}
              showSeparator={false}
              initiallyOpened={!isUserInfoFilled}
              showIncomplete={!isUserInfoFilled}
            >
              <div className="payment-user-content">
                {user?.userType == 'org' &&
                user?.companyName &&
                user?.legalType ? (
                  <ListItem
                    title={
                      v2OrgSignupEnabled
                        ? user.companyName.trim()
                        : `${user.companyName.trim()} ${
                            user?.legalType || user?.otherLegalType
                          }`
                    }
                    leftItem={
                      <img
                        style={{
                          width: 26,
                          height: 26,
                          marginRight: 35,
                          objectFit: 'contain',
                          position: 'relative',
                        }}
                        src="/images/businessPayment.png"
                        alt="user"
                      />
                    }
                  />
                ) : (
                  <>
                    {user?.firstNameVerified && user?.lastNameVerified && (
                      <ListItem
                        title={`${user.firstName.trim()} ${user.lastName.trim()} ${
                          user?.dateOfBirthVerified && user?.dateOfBirth
                            ? `, ${format(
                                new Date(user.dateOfBirth),
                                'dd.MM.yyyy'
                              )}`
                            : ''
                        }`}
                        leftItem={
                          <div>
                            {user?.firstNameVerified &&
                            user?.lastNameVerified &&
                            user?.dateOfBirthVerified ? (
                              <img
                                alt="dashedPath"
                                src="/images/userVerified.png"
                                style={{
                                  width: 28,
                                  height: 28,
                                  marginRight: 27,
                                }}
                              />
                            ) : (
                              <img
                                style={{
                                  width: 28,
                                  height: 28,
                                  marginRight: 35,
                                  objectFit: 'contain',
                                  position: 'relative',
                                }}
                                src="/images/user.png"
                                alt="user"
                              />
                            )}
                          </div>
                        }
                        greyTextColor={
                          user?.firstNameVerified && user?.lastNameVerified
                        }
                        verified={
                          user?.firstNameVerified && user?.lastNameVerified
                        }
                      />
                    )}
                    {(!user?.firstNameVerified || !user?.lastNameVerified!) && (
                      <div style={{ marginTop: 12, display: 'flex' }}>
                        <img
                          style={{
                            width: 24,
                            height: 24,
                            marginRight: 35,
                            marginTop: 3,
                            objectFit: 'contain',
                            position: 'relative',
                          }}
                          src="/images/user.png"
                          alt="user"
                        />
                        <TextField
                          required
                          id="firstName"
                          sx={{ margin: '0px 8px 0px 0px' }}
                          label={localize('payment.firstname.placeholder')}
                          helperText={
                            (formik.touched.firstName &&
                              formik.errors.firstName) ||
                            ''
                          }
                          error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                          }
                          variant="standard"
                          size="small"
                          type="text"
                          autoComplete="given-name"
                          {...formik.getFieldProps('firstName')}
                        />
                        <TextField
                          required
                          id="lastName"
                          // sx={{ width: 0.4 }}
                          label={localize('payment.lastname.placeholder')}
                          helperText={
                            (formik.touched.lastName &&
                              formik.errors.lastName) ||
                            ''
                          }
                          error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                          }
                          variant="standard"
                          size="small"
                          type="text"
                          autoComplete="family-name"
                          {...formik.getFieldProps('lastName')}
                        />
                      </div>
                    )}
                  </>
                )}
                {!(user?.dateOfBirthVerified && user?.dateOfBirth) &&
                  user?.userType !== 'org' && (
                    <div className="payment-user-date-wrapper">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          locale={de}
                        >
                          <DesktopDatePicker
                            autoFocus={false}
                            showToolbar={false}
                            inputFormat="dd.MM.yyyy"
                            label={localize('dob.dateOfBirth.label')}
                            minDate={minDate}
                            maxDate={maxDate}
                            renderInput={(params: any) => (
                              <TextField
                                data-testid="DoBView-dob"
                                ref={datePickerRef}
                                {...params}
                                variant="standard"
                                sx={{
                                  width: '100%',
                                  borderBottomStyle: 'solid',
                                }}
                                error={formik?.errors?.dateOfBirth}
                                helperText={formik?.errors?.dateOfBirth}
                              />
                            )}
                            onChange={(value) => {
                              if (!value) {
                                formik.setFieldValue('dateOfBirth', '');
                              } else {
                                formik.setFieldValue('dateOfBirth', value);
                              }
                            }}
                            value={formik?.values?.dateOfBirth}
                            mask={'dd.MM.yyyy'.replace(/[a-zA-Z]/g, '_')}
                            PopperProps={{
                              placement: 'bottom-start',
                            }}
                          />
                        </LocalizationProvider>

                        {!formik.values?.dateOfBirth && (
                          <BrandIcon
                            name="iconIncomplete"
                            style={{
                              width: 10,
                              height: 10,
                              marginLeft: '10px',
                              marginTop: '15px',
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                <ListItem
                  testid="addAddress"
                  title={
                    addressStatus === 'completed' ||
                    addressStatus === 'inProgress'
                      ? formattedAddress(address, localize)
                      : localize(
                          user?.userType === 'org'
                            ? 'settings.address.org.text'
                            : 'settings.login.data.user.add.address.text'
                        )
                  }
                  leftItem={
                    <div>
                      {user?.addressVerified ? (
                        <img
                          alt="dashedPath"
                          src="/images/addressVerified.png"
                          style={{
                            width: 28,
                            height: 28,
                            marginRight: 27,
                          }}
                        />
                      ) : (
                        <BrandIcon
                          name="home"
                          style={{
                            width: 24,
                            height: 24,
                            marginRight: 35,
                          }}
                        />
                      )}
                    </div>
                  }
                  rightIcon={
                    addressStatus === 'completed'
                      ? null
                      : addressStatus === 'inProgress'
                      ? 'arrowRight'
                      : 'iconIncomplete'
                  }
                  rightIconSize={addressStatus === 'completed' ? 14 : 10}
                  onClick={
                    addressStatus === 'open'
                      ? onPressAddress
                      : addressStatus === 'inProgress'
                      ? onPressAddressInProgress
                      : onPressAddressCompleted
                  }
                  brandTextColor={addressStatus === 'open'}
                  greyTextColor={user?.addressVerified}
                  verified={user?.addressVerified}
                />
                <div className="add-sepa-text">
                  {`${localize(
                    'firstSteps.payment.sepa.description.text'
                  ).slice(
                    0,
                    expanded
                      ? localize('firstSteps.payment.sepa.description.text')
                          .length
                      : 120
                  )}${expanded ? '' : '...'}`}
                </div>
                {!expanded ? (
                  <div
                    className="add-sepa-expand-text"
                    onClick={() => onExpandPressed(true)}
                  >
                    {localize('firstSteps.payment.sepa.url.text')}
                  </div>
                ) : (
                  <div
                    className="add-sepa-expand-text"
                    onClick={() => onExpandPressed(false)}
                  >
                    {localize('less')}
                  </div>
                )}
              </div>
            </ExpandableItem>
            {isBusinessBillingProfile && (
              <>
                <ListItem
                  title={localize('payment.profile.billing')}
                  switchItem
                  switchValue={enableBillingProfile}
                  onSwitchValueChange={() =>
                    setEnableBillingProfile(!enableBillingProfile)
                  }
                  disableSwitch={!!existingPayment}
                  showSeparator={!enableBillingProfile}
                />

                {enableBillingProfile && (
                  <>
                    <ListItem
                      title={
                        billingInfo?.company ||
                        user?.companyName ||
                        localize('payment.profile.company.add')
                      }
                      rightIcon={existingPayment ? null : 'arrowRight'}
                      rightIconSize={14}
                      leftItem={
                        <img
                          style={{
                            width: 31,
                            height: 20,
                            marginRight: 35,
                            objectFit: 'contain',
                          }}
                          src="/images/businessPayment.png"
                          alt="payin"
                        />
                      }
                      brandTextColor={
                        !billingInfo?.company && !user?.companyName
                      }
                      onClick={onPressAddBillingAddress}
                    />
                    {billingInfo?.city ? (
                      <ListItem
                        title={formattedAddress(billingInfo, localize)}
                        rightIcon={existingPayment ? null : 'arrowRight'}
                        rightIconSize={14}
                        leftIcon="home"
                        onClick={onPressAddBillingAddress}
                      />
                    ) : (
                      <ListItem
                        title={localize('payment.profile.billing.add')}
                        rightIcon={existingPayment ? null : 'arrowRight'}
                        rightIconSize={14}
                        leftIcon="home"
                        brandTextColor
                        onClick={onPressAddBillingAddress}
                      />
                    )}
                  </>
                )}
              </>
            )}

            {user?.schufaCheckRequired && (
              <div className="payment-footnote">
                <Hyperlink
                  linkStyle={{ color: getBrandColor() }}
                  linkText={(url) =>
                    url === localize('settings.payment.footnote.url')
                      ? localize('settings.payment.footnote.url.text')
                      : url
                  }
                  onPress={(url) => {
                    if (url && typeof url === 'string') Linking.openURL(url);
                  }}
                >
                  {localize(
                    'settings.payment.footnote',
                    localize('settings.payment.footnote.url')
                  )}
                </Hyperlink>
              </div>
            )}
            <div>
              <img
                className="add-sepa-logo"
                src="/images/trustLogos.png"
                alt="add-sepa-logo"
              />
            </div>
            {!existingPayment && (
              <BrandButton
                id={GA_TITLES.ON_BOARDING.ADD_SEPA}
                title={localize('firstSteps.payment.sepa.submit.text')}
                formik={formik}
                enableKeyboardEffect={!isValidIban}
              />
            )}
          </Box>
        </div>
        {isLocalizeAdded(localize, 'sepa.optimized.faq') && (
          <div className="faq">
            <Markdown rehypePlugins={[rehypeRaw]}>
              {localize('sepa.optimized.faq')}
            </Markdown>
          </div>
        )}
        <div className="screen-vertical-space" />
        <div className="screen-vertical-space" />
      </div>
    </div>
  );
};

export default React.memo(OptimizedSepaView);
